import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {debounceTime, distinctUntilChanged, Subject} from 'rxjs';
import * as L from 'leaflet';
import {BusinessLocationSelection} from './business.models';
import {extractPlusCode, recoverPlusCode} from './plus-code';

interface MapSearchResult {
  name: string;
  lat: number;
  lon: number;
}

interface NominatimSearchResult {
  display_name: string;
  lat: string;
  lon: string;
}

const locationPinIcon = L.divIcon({
  className: 'business-location-pin',
  html: `<svg width="40" height="48" viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M20 2C11.16 2 4 9.03 4 17.7C4 29.48 20 46 20 46C20 46 36 29.48 36 17.7C36 9.03 28.84 2 20 2Z" fill="#DC3545" stroke="white" stroke-width="2"/>
    <circle cx="20" cy="18" r="5.5" fill="white"/>
  </svg>`,
  iconSize: [40, 48],
  iconAnchor: [20, 46]
});

@Component({
  selector: 'app-business-location-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './business-location-modal.html',
  styleUrl: './business-location-modal.scss'
})
export class BusinessLocationModalComponent implements AfterViewInit, OnDestroy {
  @Input() latitude?: string | null;
  @Input() longitude?: string | null;
  @Input() readOnly = false;

  @ViewChild('mapContainer') private mapContainer?: ElementRef<HTMLDivElement>;

  isLoading = true;
  loadError: string | null = null;
  selectedLatitude = '';
  selectedLongitude = '';
  searchKeyword = '';
  showSearchResults = false;
  searching = false;
  searchError: string | null = null;
  searchResults: MapSearchResult[] = [];

  private map?: L.Map;
  private marker?: L.Marker;
  private readonly searchKeyword$ = new Subject<string>();
  private searchRequestId = 0;
  private readonly platformId = inject(PLATFORM_ID);

  constructor(
    public readonly activeModal: NgbActiveModal,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.searchKeyword$
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((keyword) => {
        const normalizedKeyword = keyword.trim();
        this.showSearchResults = normalizedKeyword.length > 0;
        this.searchError = null;
        if (!normalizedKeyword) {
          this.searching = false;
          this.searchResults = [];
          this.changeDetectorRef.detectChanges();
          return;
        }
        void this.searchNominatim(normalizedKeyword);
      });

    this.initializeMap();
  }

  ngOnDestroy(): void {
    this.map?.remove();
    this.map = undefined;
    this.marker = undefined;
  }

  confirmSelection(): void {
    if (this.readOnly || !this.selectedLatitude || !this.selectedLongitude) {
      return;
    }

    const selection: BusinessLocationSelection = {
      latitude: this.selectedLatitude,
      longitude: this.selectedLongitude
    };
    this.activeModal.close(selection);
  }

  searchLocation(): void {
    if (this.readOnly) {
      return;
    }
    this.searchKeyword$.next(this.searchKeyword);
  }

  clearSearchResults(): void {
    if (this.readOnly) {
      return;
    }
    this.showSearchResults = false;
    this.searching = false;
    this.searchError = null;
    this.searchResults = [];
    this.searchKeyword = '';
    this.searchKeyword$.next('');
    this.changeDetectorRef.detectChanges();
  }

  selectSearchResult(result: MapSearchResult): void {
    if (this.readOnly || !this.map) {
      return;
    }
    this.map.setView([result.lat, result.lon], 15);
    this.moveMarker(result.lat, result.lon);
    this.showSearchResults = false;
    this.changeDetectorRef.detectChanges();
  }

  private initializeMap(): void {
    if (!isPlatformBrowser(this.platformId) || !this.mapContainer?.nativeElement) {
      this.isLoading = false;
      return;
    }

    const center = this.resolveCenter();
    try {
      this.map = L.map(this.mapContainer.nativeElement, {
        zoomControl: false,
        scrollWheelZoom: true,
        minZoom: 5,
        maxZoom: 19
      }).setView(center, this.latitude && this.longitude ? 15 : 6);

      L.control.zoom({ position: 'topright' }).addTo(this.map);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(this.map);

      this.marker = L.marker(center, {
        icon: locationPinIcon,
        draggable: !this.readOnly
      }).addTo(this.map);

      if (!this.readOnly) {
        this.map.on('click', (event: L.LeafletMouseEvent) => {
          this.moveMarker(event.latlng.lat, event.latlng.lng);
        });
        this.marker.on('dragend', () => {
          const position = this.marker?.getLatLng();
          if (position) {
            this.updateSelection(position.lat, position.lng);
          }
        });
      }

      this.updateSelection(center[0], center[1]);
      this.isLoading = false;
      this.changeDetectorRef.detectChanges();
      this.refreshMapLayout();
    } catch {
      this.isLoading = false;
      this.loadError = 'ไม่สามารถโหลดแผนที่ได้ กรุณาลองใหม่อีกครั้ง';
      this.changeDetectorRef.detectChanges();
    }
  }

  private async searchNominatim(keyword: string): Promise<void> {
    if (this.readOnly) {
      return;
    }
    this.searching = true;
    this.changeDetectorRef.detectChanges();
    const requestId = ++this.searchRequestId;
    try {
      const plusCode = extractPlusCode(keyword);
      if (plusCode && plusCode.indexOf('+') === 8) {
        const coordinate = recoverPlusCode(plusCode, 0, 0);
        if (!coordinate) {
          throw new Error('รหัส Plus Code ไม่ถูกต้อง');
        }
        this.showPlusCodeLocation(plusCode, coordinate.latitude, coordinate.longitude);
        return;
      }

      const locality = plusCode ? keyword.replace(plusCode, '').trim() : keyword;
      if (plusCode && !locality) {
        throw new Error('กรุณาระบุพื้นที่ประกอบรหัส Plus Code');
      }
      const url = new URL('https://nominatim.openstreetmap.org/search');
      url.searchParams.set('q', locality || keyword);
      url.searchParams.set('format', 'json');
      url.searchParams.set('limit', '5');
      url.searchParams.set('accept-language', 'th');

      const response = await fetch(url.toString());
      if (!response.ok) {
        throw new Error('ไม่สามารถค้นหาสถานที่ได้ในขณะนี้');
      }
      const payload: NominatimSearchResult[] = await response.json();

      if (requestId !== this.searchRequestId) {
        return;
      }
      const localityResults = payload
        .filter((item) => Number.isFinite(Number(item.lat)) && Number.isFinite(Number(item.lon)))
        .map((item) => ({ name: item.display_name, lat: Number(item.lat), lon: Number(item.lon) }));
      if (plusCode && localityResults.length > 0) {
        const coordinate = recoverPlusCode(plusCode, localityResults[0].lat, localityResults[0].lon);
        if (!coordinate) {
          throw new Error('รหัส Plus Code ไม่ถูกต้อง');
        }
        this.showPlusCodeLocation(plusCode, coordinate.latitude, coordinate.longitude, localityResults[0].name);
        return;
      }
      this.searchResults = localityResults;
      this.showSearchResults = true;
    } catch (error) {
      if (requestId === this.searchRequestId) {
        this.searchResults = [];
        this.showSearchResults = true;
        this.searchError = error instanceof Error ? error.message : 'ไม่สามารถค้นหาสถานที่ได้ในขณะนี้';
      }
    } finally {
      if (requestId === this.searchRequestId) {
        this.searching = false;
        this.changeDetectorRef.detectChanges();
      }
    }
  }

  private showPlusCodeLocation(plusCode: string, latitude: number, longitude: number, locality?: string): void {
    this.map?.setView([latitude, longitude], 18);
    this.moveMarker(latitude, longitude);
    this.searchResults = [{
      name: locality ? `${plusCode} · ${locality}` : plusCode,
      lat: latitude,
      lon: longitude
    }];
    this.showSearchResults = true;
  }

  private refreshMapLayout(): void {
    const refresh = () => {
      this.map?.invalidateSize();
      window.dispatchEvent(new Event('resize'));
    };
    requestAnimationFrame(() => {
      refresh();
      window.setTimeout(refresh, 250);
    });
  }

  private resolveCenter(): [number, number] {
    const rawLatitude = `${this.latitude ?? ''}`.trim();
    const rawLongitude = `${this.longitude ?? ''}`.trim();
    const latitude = Number(rawLatitude);
    const longitude = Number(rawLongitude);

    if (rawLatitude !== '' && rawLongitude !== '' && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      return [latitude, longitude];
    }

    return [13.7563, 100.5018];
  }

  private moveMarker(lat: number, lon: number): void {
    this.marker?.setLatLng([lat, lon]);
    this.updateSelection(lat, lon);
  }

  private updateSelection(lat: number, lon: number): void {
    this.selectedLatitude = lat.toFixed(6);
    this.selectedLongitude = lon.toFixed(6);
    this.changeDetectorRef.detectChanges();
  }
}
